import { STORES, STORE_KEYS, LABELS } from '@constants'
import { saveToStorage } from '@helpers/storage.helpers'
import { Store } from '@stores'
import { saveAs } from 'file-saver'
import {
  ICompanyInvoice,
  ICompanyInvoiceClient,
  ICompanyInvoiceTask,
  ISingleDocument,
  ISingleDocumentLine,
  QuotationStatus,
} from '@typings'
import { action, makeAutoObservable, runInAction } from 'mobx'
import { FieldValues } from 'react-hook-form'
import { DatagridStore } from './datagrid.stores'
import { getFilenameFromContentDisposition } from '@helpers/getFilenameFromContentDisposition'

const defaultValues: Record<string, any> = {
  list: [],
  single: {} as ISingleDocument,
}

export class QuotationStore {
  [key: string]: any
  loading: boolean
  list: any[]
  data_grid: DatagridStore
  single: ISingleDocument
  store: Store

  constructor(store: Store) {
    makeAutoObservable(this)
    this.store = store
    this.loading = true
    this.data_grid = new DatagridStore(this.store, STORES.QUOTATION as string, {
      type: 'quotation',
    })
    this.list = []
    this.single = {} as ISingleDocument
  }

  @action
  getList = async (query?: any) => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      const params = {
        type: 'quotation',
        ...query,
      }
      const { data, meta } = await this.store.api.documents.getAll(params)
      this.set(STORE_KEYS.LIST, data)
      console.log('res: ', data)
    } catch (e) {
      console.log(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  getSingle = async (id: ISingleDocument['id']) => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      const result = await this.store.api.documents.getSingle(id)
      this.set(STORE_KEYS.SINGLE, result)
      return Promise.resolve(result)
    } catch (e) {
      console.error(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  create = async (data: FieldValues) => {
    this.set(STORE_KEYS.LOADING, true)

    try {
      if (!this.store.company.current_company_id) {
        await this.store.company.getMyCompany()
      }
      const _data: Partial<ISingleDocument> = {
        ...data,
        type: 'quotation',
        company_id: this.store.company.current_company_id,
      }
      const res = await this.store.api.documents.create(_data)
      this.set(STORE_KEYS.SINGLE, res)
      return Promise.resolve(res)
    } catch (e) {
      return Promise.reject(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  downloadPdf = async () => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      const res = await this.store.api.documents.downloadPdf(this.single.id)
      const fileName = getFilenameFromContentDisposition(res.headers['content-disposition'])

      if (fileName) {
        saveAs(res.data, fileName)
      }
    } catch (e: any) {
      let errorMessage = 'Er is iets misgegaan bij het downloaden van de PDF'
      console.log('E: ', e.text())
      try {
        // Error response is a blob, need to convert to text first
        if (e instanceof Blob) {
          const text = await e.text()
          const errorData = JSON.parse(text)
          errorMessage = errorData.message || errorMessage
        } else if (e?.response?.data?.message) {
          errorMessage = e.response.data.message
        }
      } catch {
        // If parsing fails, use default message
      }
      this.store.ui.handleErrorToast(errorMessage)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  previewPdf = async () => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      const res = await this.store.api.documents.previewPdf(this.single.id)
      return Promise.resolve(res.data)
    } catch (e: any) {
      let errorMessage = 'Er is een fout opgetreden bij het laden van de voorvertoning'
      try {
        if (e instanceof Blob) {
          const text = await e.text()
          const errorData = JSON.parse(text)
          errorMessage = errorData.error || errorData.message || errorMessage
        } else if (e?.response?.data?.error) {
          errorMessage = e.response.data.error
        } else if (e?.response?.data?.message) {
          errorMessage = e.response.data.message
        }
      } catch {
        // If parsing fails, use default message
      }
      this.store.ui.handleErrorToast(errorMessage)
      return Promise.reject(new Error(errorMessage))
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  downloadBulkPdf = async (documentIds: number[]) => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      const res = await this.store.api.documents.downloadBulkPdf(documentIds)
      const fileName = getFilenameFromContentDisposition(res.headers['content-disposition']) || 'offertes.zip'
      if (fileName) {
        saveAs(res.data, fileName)
      }
      return Promise.resolve()
    } catch (e: any) {
      let errorMessage = 'Er is iets misgegaan bij het downloaden van de PDF'
      try {
        // Error response is a blob, need to convert to text first
        if (e?.response?.data instanceof Blob) {
          const text = await e.response.data.text()
          const errorData = JSON.parse(text)
          errorMessage = errorData.message || errorMessage
        } else if (e?.response?.data?.message) {
          errorMessage = e.response.data.message
        }
      } catch {
        // If parsing fails, use default message
      }
      return Promise.reject(new Error(errorMessage))
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  /**
   * Check if the given quotation IDs can be bulk downloaded
   * Only finalized quotations can be downloaded
   */
  canBulkDownload = (documentIds: number[]): boolean => {
    if (documentIds.length === 0) return false

    return documentIds.every(id => {
      const quotation = this.data_grid.current_rows.find((row: any) => row.id === id)
      return quotation?.is_finalised === true || quotation?.lifecycle_status === 'finalized'
    })
  }

  @action
  finalise = async () => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      await this.store.ui.confirm({
        title: 'Offerte definitief maken?',
        content:
          // eslint-disable-next-line max-len
          'Door akkoord te gaan maak je de offerte definitief. Je kan deze hierna niet meer wijzigen. Weet je zeker dat je de offerte definitief wilt maken?',
        withCancel: true,
      })
      const res = await this.store.api.documents.finalise(this.single.id, this.single)
      this.set(STORE_KEYS.SINGLE, res)
      return Promise.resolve(res)
    } catch (e) {
      return Promise.reject(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  update = async (data: FieldValues) => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      const res = await this.store.api.documents.update(this.single.id, data as ISingleDocument)
      this.set(STORE_KEYS.SINGLE, res)
      return Promise.resolve(res)
    } catch (e) {
      return Promise.reject(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  duplicate = async (id: ISingleDocument['id']) => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      await this.store.ui.confirm({
        title: LABELS.CONFIRM_DUPLICATE_QUOTATION_TITLE,
        content: LABELS.CONFIRM_DUPLICATE_QUOTATION_TEXT,
        withCancel: true,
      })

      const res = await this.store.api.documents.duplicate(id)
      return Promise.resolve(res)
    } catch (e) {
      return Promise.reject(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  convertToInvoice = async (id: ISingleDocument['id']) => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      await this.store.ui.confirm({
        title: 'Offerte omzetten naar factuur?',
        content:
          'Er wordt een nieuwe factuur aangemaakt op basis van deze offerte. ' +
          'De offerte blijft behouden. Wil je doorgaan?',
        withCancel: true,
      })

      const res = await this.store.api.documents.duplicate(id, { type: 'invoice' })
      return Promise.resolve(res)
    } catch (e) {
      return Promise.reject(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  updateQuotationStatus = async (newStatus: QuotationStatus, comment?: string) => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      const res = await this.store.api.documents.updateQuotationStatus(this.single.id, {
        quotation_status: newStatus,
        quotation_status_comment: comment,
      })
      this.set(STORE_KEYS.SINGLE, res)
      return Promise.resolve(res)
    } catch (e) {
      return Promise.reject(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  getQuotationStatusHistory = async (id: ISingleDocument['id']) => {
    try {
      const res = await this.store.api.documents.getQuotationStatusHistory(id)
      return Promise.resolve(res)
    } catch (e) {
      return Promise.reject(e)
    }
  }

  @action
  reset = (withLoading?: boolean) => {
    runInAction(() =>
      Object.keys(defaultValues).map(key => {
        this.set(key, defaultValues[key])
      })
    )
    if (withLoading) this.set(STORE_KEYS.LOADING, false)
  }

  @action
  set = (target: string, value: unknown, save?: boolean): unknown => {
    if (!target) return
    this[target] = value
    if (save) saveToStorage(target, value)
  }
}
